import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Hysteresis thresholds, matching Agentic Studio's networks list. */
const ENTER_AT = 50;
const EXIT_BELOW = 31;

/** How long scroll events are ignored after a transition. */
const SETTLE_MS = 300;

/** Hover held this long over a compressed bar brings the toolbar back. */
const HOVER_REVEAL_MS = 500;

/**
 * Scroll-aware sticky-header state for a list page's compressing header.
 *
 * Originates in Agentic Studio's networks list. It lived as a per-app copy in
 * Dashboards and Unified Profiles until those drifted apart in their docblocks;
 * it is pure behaviour with no markup and no layout opinion, so it belongs here
 * where every app already looks for hooks.
 *
 * It takes a **ref** to the scroll container rather than looking one up by id:
 * Studio and Governance can use `document.getElementById(...MAIN_CONTENT_ID)`
 * because their shell owns a single scroll container, but a page that owns its
 * own scroller has no id to look up.
 *
 * Keep the four constants as they are — each fixes a specific bug described
 * below, and retuning one silently reintroduces it.
 *
 * The hysteresis is the whole point: compressing at the same offset where
 * expanding happens makes the header oscillate, because the bar changing height
 * moves `scrollTop` underneath you. Entering at 50 and leaving below 31 keeps
 * one gesture from crossing both. The `SETTLE_MS` lock covers the rest — the
 * browser's own scroll anchoring adjusts `scrollTop` right after the height
 * changes, which reads as a second scroll event.
 *
 * The hover pair is the other half of the behaviour: once the bar is
 * compressed its toolbar is gone, and resting the pointer on the bar for half a
 * second brings it back. The delay is deliberate — without it the toolbar
 * flickers in every time the pointer crosses the bar on its way somewhere else.
 *
 * @param {import('react').RefObject<HTMLElement>} scrollRef
 * @returns {{ isScrolled: boolean, filtersVisible: boolean,
 *   onMouseEnter: () => void, onMouseLeave: () => void }}
 */
export function useStickyScroll(scrollRef) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const lockedRef = useRef(false);
  const hoverTimerRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const resolve = (top) => (previous) => {
      if (top > ENTER_AT) return true;
      if (top < EXIT_BELOW) return false;
      return previous;
    };

    const onScroll = () => {
      if (lockedRef.current) return;
      setIsScrolled(resolve(el.scrollTop));
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  useEffect(() => {
    lockedRef.current = true;
    const timer = setTimeout(() => {
      lockedRef.current = false;
      const el = scrollRef.current;
      if (!el) return;
      const { scrollTop } = el;
      setIsScrolled((previous) => {
        if (scrollTop > ENTER_AT) return true;
        if (scrollTop < EXIT_BELOW) return false;
        return previous;
      });
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [isScrolled, scrollRef]);

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  const onMouseEnter = useCallback(() => {
    hoverTimerRef.current = setTimeout(() => setFiltersVisible(true), HOVER_REVEAL_MS);
  }, []);

  const onMouseLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    setFiltersVisible(false);
  }, []);

  return useMemo(
    () => ({ isScrolled, filtersVisible, onMouseEnter, onMouseLeave }),
    [isScrolled, filtersVisible, onMouseEnter, onMouseLeave],
  );
}
